import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FilterOptionsState } from "@mui/material/useAutocomplete";

import {
  APIListValuesResponse,
  APISuccessResponse,
  genericValuesCallback,
} from "shared/api/api";
import { useDebounce } from "shared/hooks";

import { getFiltersQuery } from "features/ui/Filters/FilterBuilder/utils";
import { FilterOperator } from "features/ui/Filters/types";
import { Option, SelectOption } from "features/ui/Select";

import {
  DEBOUNCE_DELAY,
  DEFAULT_APPEND_ARBITRARY_OPTION,
  DEFAULT_DISABLE_ARBITRARY_TEXT,
  DEFAULT_FORMAT_FUNCTION,
  DEFAULT_LOAD_NUM_ITEMS,
  DEFAULT_LOAD_OPTIONS_ARGS,
} from "./constants";
import { SelectFilterProps } from "./types";
import {
  combineApiAndSelectedOptions,
  createOnChangeHandler,
  getNewItemOption,
  getNoResultsMessage,
  getOptions,
  mapDistinctValuesToOptions,
  NEW_ITEM_OPTION_ID,
  transformOptions,
} from "./utils";

export const useMultipleSelectFilter = ({
  fieldName,
  initialSelected = [],
  maxValues,
  search,
  loadDataOnOpen = false,
  loadOptionsFunc,
  loadOptionsArgs = DEFAULT_LOAD_OPTIONS_ARGS,
  onChange,
  onFilterChange,
  transformInitialSelectedFunc,
  formatLabelFunc = DEFAULT_FORMAT_FUNCTION,
  fieldNameForAPI,
  disableFiltering,
  enableMinMaxFilters,
  includeEmptyOption,
  staticFilters = [],
  filterType,
  filterDataType,
  customFilter,
  defaultLoadLimit = DEFAULT_LOAD_NUM_ITEMS,
  disableArbitraryText = DEFAULT_DISABLE_ARBITRARY_TEXT,
  appendArbitraryOption = DEFAULT_APPEND_ARBITRARY_OPTION,
  autoFocus,
  valuesEndpoint,
}: SelectFilterProps) => {
  const { t } = useTranslation("filters");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(!!autoFocus);
  // options loaded from the API
  const [options, setOptions] = useState<SelectOption[]>([]);
  // current value of the input used for searching
  const [input, setInput] = useState("");

  const initialSelectedWithoutNull =
    initialSelected.length > 0 && initialSelected[0].id === "null"
      ? []
      : initialSelected;

  const [initialSelectedOption, setInitialSelectedOption] = useState(
    initialSelectedWithoutNull
  );
  const [initialized, setInitialized] = useState(
    transformInitialSelectedFunc === undefined || !initialSelected.length
  );

  // temporary selection while the dropdown is open
  const [tempSelected, setTempSelected] = useState<SelectOption<Option>[]>(
    initialSelectedOption
  );

  const onTempSelected = (values: SelectOption[]) => {
    setTempSelected(values);
    setInitialSelectedOption(values);
  };

  const debouncedInput = useDebounce(input, DEBOUNCE_DELAY);

  const filtersQuery = !disableFiltering
    ? getFiltersQuery(customFilter, staticFilters)
    : getFiltersQuery(customFilter);

  useEffect(() => {
    if (initialSelected.length > 0 && transformInitialSelectedFunc) {
      transformInitialSelectedFunc(initialSelected)
        .then((options: SelectOption[]) => {
          setInitialSelectedOption(formatLabelFunc(options));
        })
        .catch(() => {
          setInitialSelectedOption([]);
          onFilterChange &&
            onFilterChange({
              key: fieldName,
              op_id: FilterOperator.IN,
              values: [],
              dataType: filterDataType,
            });
        })
        .finally(() => {
          setInitialized(true);
        });
    }
    // handle scenario when values are removed
    else if (initialSelected?.length === 0) {
      setInitialSelectedOption([]);
    }

    setInitialSelectedOption(formatLabelFunc(initialSelected));
    // we do not want to fetch information every render of component so we
    // limit to only when elements change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldName, initialSelected.length]);

  const fetchOptions = (
    args: Parameters<NonNullable<typeof loadOptionsFunc>>[0]
  ): Promise<SelectOption[]> | undefined => {
    if (loadOptionsFunc) {
      return loadOptionsFunc(args);
    }

    if (valuesEndpoint) {
      return genericValuesCallback(valuesEndpoint)(args).then(
        (data: APISuccessResponse<APIListValuesResponse>) =>
          mapDistinctValuesToOptions(data.data?.distinctValues)
      );
    }

    return undefined;
  };

  useEffect(() => {
    // don't search on an empty query
    if (!debouncedInput && !loadDataOnOpen) {
      return;
    }

    setLoading(true);

    fetchOptions({
      fieldName: fieldNameForAPI || fieldName,
      like: debouncedInput,
      filter: filtersQuery,
      limit: debouncedInput === "" ? defaultLoadLimit : undefined,
      ...loadOptionsArgs,
    })
      ?.then((options: SelectOption[]) => {
        const combinedOptions = combineApiAndSelectedOptions(
          options,
          tempSelected
        );
        setOptions(
          getOptions(
            t,
            formatLabelFunc(combinedOptions),
            filterType,
            enableMinMaxFilters,
            includeEmptyOption
          )
        );
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedInput,
    fieldName,
    fieldNameForAPI,
    loadOptionsArgs,
    filtersQuery,
    filterType,
    enableMinMaxFilters,
    loadDataOnOpen,
    defaultLoadLimit,
    valuesEndpoint,
  ]);

  const loadOptions = () => {
    // only load the options once
    if (options.length !== 0 || (search && !loadDataOnOpen)) {
      setOpen(true);

      return;
    }

    setLoading(true);

    fetchOptions({
      fieldName: fieldNameForAPI || fieldName,
      limit: loadDataOnOpen ? defaultLoadLimit : undefined,
      filter: filtersQuery,
      ...loadOptionsArgs,
    })
      ?.then((options: SelectOption[]) => {
        const combinedOptions = combineApiAndSelectedOptions(
          options,
          tempSelected
        );
        setOptions(
          getOptions(
            t,
            formatLabelFunc(combinedOptions),
            filterType,
            enableMinMaxFilters,
            includeEmptyOption
          )
        );
        setOpen(true);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOnChange = (values: SelectOption[]) => {
    const uniqueValues = Array.from(
      new Set(values.map(({ id }) => id.toString()))
    );

    setTempSelected(values);
    onChange && onChange(uniqueValues);
    onFilterChange &&
      onFilterChange({
        key: fieldName,
        op_id: FilterOperator.IN,
        values: uniqueValues,
        dataType: filterDataType,
      });
  };

  const noResultsMessage = getNoResultsMessage(t, {
    loading,
    options,
    search,
    input,
  });

  const selectedValues: SelectOption[] = (() => {
    if (!initialSelectedOption) return [];

    return transformOptions(
      t,
      initialSelectedOption,
      filterType,
      enableMinMaxFilters,
      includeEmptyOption
    );
  })();

  const onChangeHandler = createOnChangeHandler({
    handleOnChange,
    setInitialSelectedOption,
    maxValues,
  });

  const handleFilterOptions = (
    options: SelectOption[],
    state: FilterOptionsState<SelectOption>
  ) => {
    const searchValue = input.toLowerCase();

    const filteredOptions = options.filter((option) => {
      if (option.value === null) return false;

      return option.value.toString().toLowerCase().includes(searchValue);
    });

    const { inputValue } = state;
    const isExisting = options.some((option) => inputValue === option.value);

    if (
      appendArbitraryOption &&
      !disableArbitraryText &&
      inputValue !== "" &&
      !isExisting
    ) {
      filteredOptions.push(
        getNewItemOption(t, inputValue, options.length === 0)
      );
    }

    return filteredOptions;
  };

  const handleOpen = () => {
    // Always reorder options when opening dropdown
    if (options.length > 0) {
      const combinedOptions = combineApiAndSelectedOptions(
        options,
        selectedValues
      );
      // Don't call getOptions here - it would add duplicate special options
      // The special options (MIN/MAX/Empty) were already added when data was fetched
      setOptions(formatLabelFunc(combinedOptions));
    }

    setTempSelected(selectedValues);
    setOpen(true);
    loadOptions();
  };

  const handleClose = () => {
    if (!open) return;

    setOpen(false);
    handleOnChange(tempSelected); // commit temporary selection
  };

  const handleToggle = () => {
    if (open) {
      handleClose();
    } else {
      handleOpen();
    }
  };

  const filterOptions = (
    options: SelectOption[],
    state: FilterOptionsState<SelectOption>
  ) => (search ? handleFilterOptions(options, state) : options);

  const isMaxValueLimitReached = Boolean(
    maxValues && selectedValues.length >= maxValues
  );

  const handleCustomOptionChange = (vals: SelectOption[]) => {
    if (open) {
      onTempSelected(vals);

      return;
    }

    handleOnChange(vals);
  };

  const handleSelectAll = useCallback(() => {
    // get all currently visible/filtered options
    const filteredOptions = handleFilterOptions(options, {
      inputValue: input,
      getOptionLabel: (option: SelectOption) => option.value.toString(),
    });

    // filter out default (placeholder) option
    const validOptions = filteredOptions.filter(
      (option) => option.id !== NEW_ITEM_OPTION_ID
    );

    setTempSelected(validOptions);
    setInitialSelectedOption(validOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, input]);

  const handleClear = useCallback(() => {
    setTempSelected([]);
    setInitialSelectedOption([]);
  }, []);

  return {
    input,
    setInput,
    selectedValues,
    open,
    loading,
    setLoading,
    options,
    setOptions,
    tempSelected,
    initialized,
    onTempSelected,
    noResultsMessage,
    onChangeHandler,
    handleOnChange,
    handleOpen,
    handleClose,
    handleToggle,
    filterOptions,
    isMaxValueLimitReached,
    handleCustomOptionChange,
    handleSelectAll,
    handleClear,
  };
};
